import clsx from 'clsx'
import PropTypes from 'prop-types'

import { TicketOpenStatus, TicketStatusOptions } from '../../../../shared/const'
import useAttentionPulse, { PULS_KLASSE } from '../../../../shared/useAttentionPulse'

import styles from './donut.module.scss'

/*
 * Farben der sieben Stufen.
 *
 * Nicht frei gewählt: Die Palette ist gegen Farbfehlsichtigkeit geprüft
 * (dataviz-Validator, Modus hell). Die beiden harten Kriterien — Trennung bei
 * Protanopie/Tritanopie und der Abstand für Normalsichtige — halten; das
 * schwächste benachbarte Paar ist unverändert Erledigt↔Verworfen mit ΔE 15,7.
 * Geprüft wurde die Reihenfolge des Rings, und die Nachbarschaften darin liegen
 * alle darüber. „In Arbeit" ist am 22.08.2026 dazugekommen (die Kette gilt
 * seither für alle Arten): Es sitzt zwischen Türkis und Violett und ist deshalb
 * **warm** — keine bestehende Farbe hat sich dabei verschoben, die geprüften
 * Paare gelten unverändert.
 *
 * Für den Kontrast zur Fläche warnt der Validator, und die Auflage daraus ist
 * erfüllt: Jedes Segment trägt in der Legende sein Wort und seine Zahl —
 * Identität hängt nie an der Farbe allein.
 *
 * „Verworfen" ist bewusst grau: ein abgeschalteter Zustand, keine eigene
 * Kategorie. Genau das lässt es durch den Chroma-Test fallen, und genau so ist
 * es gemeint.
 *
 * Wo es geht, dieselbe Farbe wie im Trichter über „Alle Anforderungen"
 * (UI/StatusFunnel): Klärung orange, Eingeplant türkis, Abgeschlossen grün. „Zur
 * Bestätigung" kann dessen Blau nicht bekommen — das trägt hier „Neu" — und
 * wird deshalb violett.
 */
const FARBE = {
	NEW: '#1c5a86',
	CLARIFICATION: '#ef8400',
	READY: '#00a0af',
	IN_DEVELOPMENT: '#c8912a',
	REVIEW: '#7b52a1',
	ACCEPTED: '#45b07a',
	REJECTED: '#b0b8bb'
}

const GROESSE = 132
const DICKE = 18
const RADIUS = (GROESSE - DICKE) / 2
const UMFANG = 2 * Math.PI * RADIUS
// Ein schmaler Spalt zwischen den Segmenten - ohne ihn verschwimmen zwei
// gleichfarbig wirkende Nachbarn zu einem Bogen.
const SPALT = 2

/**
 * Verteilung auf die sieben Stufen, als Ring.
 *
 * Tritt in den Zuweisungs-Reitern an die Stelle des Funnels. Der Funnel zeigt
 * den Ablauf des ganzen Bestands; bei dem, was jemandem gehört, ist nicht der
 * Weg die Frage, sondern die Verteilung: Wie viel liegt noch an, wie viel
 * läuft, wie viel ist weg. Dafür ist der Ring die richtige Form, und er ist
 * auf einen Blick zu erfassen.
 *
 * Jedes Segment und jede Zeile der Legende filtert — wie die Stufen des
 * Funnels, mehrere gleichzeitig möglich.
 */
export default function StatusDonut({ counts, value, onChange }) {
	const active = value || []
	// Wie beim Trichter: Die gewaehlten Stufen leuchten kurz auf, sonst haelt man
	// die Legende fuer eine reine Anzeige und die kurze Liste fuer den ganzen Bestand.
	const puls = useAttentionPulse(active.length > 0)

	const stufen = TicketStatusOptions.map(option => ({
		...option,
		count: counts[option.value] || 0
	}))

	const gesamt = stufen.reduce((sum, stufe) => sum + stufe.count, 0)
	// Alles bis einschließlich „Zur Abnahme" — die Zahl in der Mitte beantwortet
	// die Frage, wegen der man hersieht, und zählt dasselbe wie die Zahl am
	// Reiter und die wöchentliche Erinnerung.
	const offen = stufen
		.filter(stufe => TicketOpenStatus.includes(stufe.value))
		.reduce((sum, stufe) => sum + stufe.count, 0)

	const toggle = statusValue => {
		const next = active.includes(statusValue)
			? active.filter(item => item !== statusValue)
			: [...active, statusValue]
		onChange(next)
	}

	// Anteile in Bogenlängen. Ohne Daten bleibt der Ring leer und grau —
	// ein Vollkreis in einer Farbe wäre eine Aussage, die es nicht gibt.
	let versatz = 0
	const boegen = stufen.map(stufe => {
		const laenge = gesamt ? (stufe.count / gesamt) * UMFANG : 0
		const bogen = { ...stufe, laenge, versatz }
		versatz += laenge
		return bogen
	})

	return (
		<div className={styles.root}>
			<div className={styles.chart}>
				<svg
					width={GROESSE}
					height={GROESSE}
					viewBox={`0 0 ${GROESSE} ${GROESSE}`}
					role="img"
					aria-label={`Anforderungen nach Status: ${stufen
						.map(stufe => `${stufe.label} ${stufe.count}`)
						.join(', ')}`}
				>
					{/* Bei 12 Uhr beginnen, im Uhrzeigersinn. */}
					<g transform={`rotate(-90 ${GROESSE / 2} ${GROESSE / 2})`}>
						<circle
							cx={GROESSE / 2}
							cy={GROESSE / 2}
							r={RADIUS}
							fill="none"
							stroke="#eef2f4"
							strokeWidth={DICKE}
						/>
						{boegen
							.filter(bogen => bogen.count > 0)
							.map(bogen => {
								const gedimmt = active.length > 0 && !active.includes(bogen.value)
								// Der Spalt darf ein winziges Segment nicht auffressen.
								const sichtbar = Math.max(0, bogen.laenge - SPALT)

								return (
									<circle
										key={bogen.value}
										cx={GROESSE / 2}
										cy={GROESSE / 2}
										r={RADIUS}
										fill="none"
										stroke={FARBE[bogen.value]}
										strokeWidth={DICKE}
										strokeDasharray={`${sichtbar} ${UMFANG - sichtbar}`}
										strokeDashoffset={-bogen.versatz}
										opacity={gedimmt ? 0.25 : 1}
										className={styles.segment}
										onClick={() => toggle(bogen.value)}
									>
										<title>{`${bogen.label}: ${bogen.count}`}</title>
									</circle>
								)
							})}
					</g>

					<text className={styles.mitteZahl} x="50%" y="47%" textAnchor="middle">
						{offen}
					</text>
					<text className={styles.mitteWort} x="50%" y="63%" textAnchor="middle">
						offen
					</text>
				</svg>
			</div>

			{/*
			 * Die Legende ist zugleich der Filter. Sie trägt Wort und Zahl — die
			 * Farbe steht daneben, nicht an ihrer Stelle.
			 */}
			<ul className={styles.legende}>
				{stufen.map(stufe => {
					const isActive = active.includes(stufe.value)
					const gedimmt = active.length > 0 && !isActive

					return (
						<li key={stufe.value}>
							<button
								type="button"
								aria-pressed={isActive}
								className={clsx(
									styles.eintrag,
									isActive && styles.eintragActive,
									isActive && puls.leuchtet && PULS_KLASSE,
									gedimmt && styles.eintragDimmed
								)}
								onAnimationEnd={puls.onAnimationEnd}
								onClick={() => toggle(stufe.value)}
							>
								<span
									className={styles.punkt}
									style={{ background: FARBE[stufe.value] }}
									aria-hidden="true"
								/>
								<span className={styles.wort}>{stufe.label}</span>
								<span className={styles.zahl}>{stufe.count}</span>
							</button>
						</li>
					)
				})}
			</ul>

			<p className={styles.hinweis}>
				{gesamt === 0
					? 'In dieser Sicht ist nichts zugewiesen.'
					: 'Auf eine Stufe klicken, um die Liste darauf einzuschränken.'}
			</p>
		</div>
	)
}

StatusDonut.propTypes = {
	// { NEW: 4, READY: 1, … } — fehlende Stufen zählen als 0.
	counts: PropTypes.object,
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired
}

StatusDonut.defaultProps = {
	counts: {},
	value: []
}
